import React, { ReactNode, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Slide,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Autocomplete,
  Chip,
  TextField,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TransitionProps } from "@mui/material/transitions";
import Button from "../../../../components/Button/Button";
import { Formik, Form, Field, FieldProps } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { setToast } from "../../../../store/slices/toastSlice";
import API from "../../../../api/axios";
import AutoAwesomeMotionOutlinedIcon from "@mui/icons-material/AutoAwesomeMotionOutlined";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

interface ManageDomainsModal {
  open?: boolean;
  loading?: boolean;
  title: string;
  summary?: string | ReactNode;
  onClose?: () => void;
  onConfirm?: () => void;
  mappedDomains?: any;
  interviewerRole?: boolean; 
}

type FormValues = {
    domains?: Array<{ id: number; name: string }>;
    currentDomains?: { id: number; name: string } | null;
    newDomains?: { id: number; name: string } | null;
  };

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ManageDomainsModal: React.FC<ManageDomainsModal> = ({
  open = false,
  loading = false,
  onClose,
  onConfirm,
  title,
  mappedDomains,
  interviewerRole = false
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [domainData, setDomainData] = useState([]);
  const [selectedOption, setSelectedOption] = useState("add");
  const [filterSelectedDomains, setfilterSelectedDomains] = useState<any>([]);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    const fetchDomainData = async () => {
      try {
        const domainResponse = await API.get(
          "/items/master_generals?fields=id,type,name&filter[type]=domain&filter[status]=published&sort[]=name&limit=-1"
        );
        setDomainData(domainResponse?.data?.data);
      } catch (error) {
        // Failed to fetch domain data
      }
    };
    if(interviewerRole){
      fetchDomainData();
    }
  },[interviewerRole]);

  const validationSchema = Yup.object(
    selectedOption !== "replace" ?{
    domains: Yup.array()
    .min(1, "At least one domain is required") 
    .required("Domains required")
  } : 
  {  
    currentDomains: Yup.object()
    .required("Domain to be deleted is required"), 
    newDomains: Yup.object()
    .required("Domain to be added is required"),}
)

  let mappedDomainsValues: any;
  let unmappedDomainsValues: any;
  mappedDomainsValues = mappedDomains?.map((domain: any) => domain?.domain_id);

  if (mappedDomainsValues) {
    unmappedDomainsValues = domainData.filter(
      (masterDomain: any) =>
        !mappedDomainsValues.some(
          (domain: any) => domain.id === masterDomain.id
        )
    );
  }

  // console.log(mappedDomainsValues, "mappedDomainsValues");
  // console.log(unmappedDomainsValues, "unmappedDomainsValues");

const initialValues: FormValues =
  selectedOption === "replace"
    ? { currentDomains: null, newDomains: null }
    : { domains: [] };

  const handleSubmit = async (values: typeof initialValues) => {
    let currentDomains:any;
    let newDomainReplace:any;
    let newDomains:any;
    let removeDomains:any;
    // console.log(values, "form submitted");
    
    if(selectedOption === "add"){
      newDomains = values?.domains?.map((domain:any)=> domain?.name)
    }
    if(selectedOption === "delete"){
        removeDomains = values?.domains?.map((domain:any)=> domain?.name)
    }
    if(selectedOption === "replace"){
        currentDomains =[values?.currentDomains?.name]
        newDomainReplace = [values?.newDomains?.name]
    }

    const interviewerDomainNotification= async () => {
      setIsLoading(true);
      try {
        const domainResponse = await API.post(
          "/notification/interviwer/domains",
          {
            currentDomains : selectedOption === "replace" ? currentDomains : null , 
            newDomains : selectedOption === "add" ?  newDomains : selectedOption === "replace" ? newDomainReplace : null,
            removeDomains : selectedOption === "delete" ?  removeDomains : null , 
          }
        );

      if (domainResponse?.status == 200 || domainResponse?.statusText == "OK") {
            dispatch(
              setToast({
                message: domainResponse?.data?.message || "Request for domain change notification sent",
                severity: "success",
                open: true,
              })
            );}

      } catch (error) {
        // Notification send failure is non-critical to domain mapping state
      }finally{
        setIsLoading(false);
        onClose?.()
      }
    };
    
    if(interviewerRole){
      interviewerDomainNotification();
    }
    
  };
  
  const handleOptionChange = (event: any) => {
    setSelectedOption(event.target.value);
  };

  return (
    <Dialog
      maxWidth={"sm"}
      fullWidth
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          overflow: "visible",
          borderRadius: "15px",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "solid 1px #ddd",
          position: "relative",
          fontSize: { xs: "1rem", md: "1.1rem" },
        }}
      >
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: { xs: "-15px", md: "-20px" },
            top: { xs: "-15px", md: "-20px" },
            height: { xs: "34px", md: "44px" },
            width: { xs: "34px", md: "44px" },
            color: "#FB8B0E",
            border: "2px solid #FB8B0E",
            borderRadius: "50% !important",
            padding: 0,
            backgroundColor: "#fff",
            zIndex: 1000,
          }}
          disableRipple
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          height: "auto",
        }}
      >
        <FormControl>
          <RadioGroup
            aria-labelledby="domains"
            defaultValue="Add Domain"
            name="domains"
            row
            sx={{ color: "#000", marginY: "1rem" }}
            onChange={handleOptionChange}
            value={selectedOption}
          >
            {/* Add Domain */}
            <FormControlLabel
              value="add"
              control={<Radio size="small" />}
              label="Add Domain"
              sx={{
                "& .MuiFormControlLabel-label": {
                  color: "#000",
                  fontSize: "14px",
                  fontWeight: "600",
                },
              }}
            />
            {/* Delete Domain */}
            <FormControlLabel
              value="delete"
              control={<Radio size="small" />}
              label="Delete Domain"
              sx={{
                "& .MuiFormControlLabel-label": {
                  color: "#000",
                  fontSize: "14px",
                  fontWeight: "600",
                },
              }}
            />
            {/* Request For Domain Change */}
            <FormControlLabel
              value="replace"
              control={<Radio size="small" />}
              label="Request For Domain Change"
              sx={{
                "& .MuiFormControlLabel-label": {
                  color: "#000",
                  fontSize: "14px",
                  fontWeight: "600",
                },
              }}
            />
          </RadioGroup>
        </FormControl>
        <Formik
          key={selectedOption}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
         {({handleSubmit, values,  touched, errors,isValid, dirty, setFieldTouched, setFieldValue})=>(
            <Form onSubmit={handleSubmit}>
            {selectedOption !== "replace" ? (
                    <Field name="domains">
                      {({ field, form }: FieldProps) => {
                        const toggleShowMore = () => {
                          setShowMore((prev) => !prev);
                        };
                        const handleDelete = (option: any) => {
                          const updatedValue = field.value.filter(
                            (item: any) =>
                              item?.id !== option?.id ||
                              item?.domain_id !== option?.domain_id
                          );
                          form.setFieldValue("domains", updatedValue);
                        };
                        const visibleTags = (field?.value || []).slice(0, 3);
                        const hiddenTags = (field?.value || []).slice(3);
                        return (
                          <Autocomplete
                            id="multiple-limit-tags"
                            multiple
                            limitTags={3}
                            options={
                              selectedOption === "add"
                                ? unmappedDomainsValues
                                : mappedDomainsValues
                            }
                            componentsProps={{
                              popper: {
                                disablePortal: true, // Optional, keeps the listbox within the DOM tree
                              },
                            }}
                            sx={{ maxHeight: "500px", 
                              // paddingY:"51px" 
                            }}
                            onFocus={() => {
                              setShowMore(true);
                            }}
                            onBlur={() => {
                              setShowMore(false);
                            }}
                            getOptionLabel={(option: any) =>
                              option.name || "NA"
                            }
                            value={field.value || []}
                            onChange={(_, newValue) => {
                              form.setFieldValue("domains", newValue as Array<{ id: number; name: string }>);
                            }}
                            disableListWrap
                            filterSelectedOptions
                            renderTags={(value: any[], getTagProps) => (
                              <>
                                {visibleTags.map((option: any, index: any) => {
                                  const label =
                                    option.name ||
                                    // domainData.find(
                                    //     (item: any) => item.id === option.domain_id
                                    // )?.name ||
                                    "NA";
                                  return (
                                    <Chip
                                      {...getTagProps({ index })}
                                      key={option.id || option.domain_id}
                                      variant="outlined"
                                      label={label}
                                      deleteIcon={
                                        <CloseIcon
                                          onMouseDown={(event) => {
                                            event.preventDefault(); // Prevent focus loss
                                          }}
                                        />
                                      }
                                      onDelete={(event) => {
                                        event.stopPropagation();
                                        handleDelete(option);
                                      }}
                                      sx={{
                                        borderColor: "#fb8b0e",
                                        color: "#fb8b0e",
                                        "& .MuiChip-deleteIcon": {
                                          color: "#fb8b0e",
                                        },
                                      }}
                                    />
                                  );
                                })}
                                {showMore &&
                                  hiddenTags.map((option: any, index: any) => {
                                    const label =
                                      option.name ||
                                      // domainData.find(
                                      //     (item: any) => item.id === option.domain_id
                                      // )?.name ||
                                      "NA";
                                    return (
                                      <Chip
                                        {...getTagProps({
                                          index: visibleTags.length + index,
                                        })}
                                        key={option.id || option.domain_id}
                                        variant="outlined"
                                        label={label}
                                        deleteIcon={
                                          <CloseIcon
                                            onMouseDown={(event) => {
                                              event.preventDefault(); // Prevent focus loss
                                            }}
                                          />
                                        }
                                        onDelete={(event) => {
                                          event.stopPropagation();
                                          handleDelete(option);
                                        }}
                                        sx={{
                                          borderColor: "#fb8b0e",
                                          color: "#fb8b0e",
                                          "& .MuiChip-deleteIcon": {
                                            color: "#fb8b0e",
                                          },
                                        }}
                                      />
                                    );
                                  })}
                                {!showMore && hiddenTags.length > 0 && (
                                  <Chip
                                    label={`${hiddenTags.length} more`}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      toggleShowMore();
                                    }}
                                    sx={{
                                      backgroundColor: "#fb8b0e",
                                      color: "#fff",
                                    }}
                                  />
                                )}
                              </>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Domain"
                                variant="outlined"
                                fullWidth                                
                                onBlur={() => {
                                  form.setFieldTouched("domains", true);
                                }}
                              />
                            )}
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                          />
                        );
                      }}
                    </Field>
            ) : (
              <>
                {/* fromReplace */}
                <Autocomplete
                  //   size="small"
                  fullWidth
                  disablePortal
                  options={mappedDomainsValues || []}
                  getOptionLabel={(option: any) => option?.name || ""}
                value={values.currentDomains}
                disableListWrap
                onChange={(event, newValue) => {
                    setFieldValue("currentDomains", newValue);
                  }}
                  componentsProps={{
                    clearIndicator: { disableRipple: true },
                    popupIndicator: { disableRipple: true },
                  }}
                  sx={{
                    // width: { xs: "50%", sm: "150px", md: "150px" },
                    "& .MuiAutocomplete-inputRoot": {
                      backgroundColor: "white",
                      borderRadius: "10px",
                    },
                    "&:hover:": {
                      border: "transparent",
                    },
                    marginY: "1rem",
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Remove Domain"
                      variant="outlined"
                      placeholder="Status"
                      InputProps={{
                        ...params.InputProps,
                        sx: {
                          backgroundColor: "white",
                          padding: "6px 12px",
                          "&:hover": {
                            borderColor: "#fff",
                          },
                        },
                      }}
                    />
                  )}
                />

                {/* to Replace */}
                <Autocomplete
                  fullWidth
                  // size="small"
                  disablePortal
                  options={unmappedDomainsValues || []}
                  getOptionLabel={(option: any) => option?.name || ""}
                value={values.newDomains}
                disableListWrap
                onChange={(event, newValue) => {
                    setFieldValue("newDomains", newValue);
                  }}
                  componentsProps={{
                    clearIndicator: { disableRipple: true },
                    popupIndicator: { disableRipple: true },
                  }}
                  sx={{
                    // width: { xs: "50%", sm: "150px", md: "150px" },
                    "& .MuiAutocomplete-inputRoot": {
                      backgroundColor: "white",
                      borderRadius: "10px",
                    },
                    "&:hover:": {
                      border: "transparent",
                    },
                    marginY: "1rem",
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add Domain"
                      variant="outlined"
                      placeholder="Status"
                      InputProps={{
                        ...params.InputProps,
                        sx: {
                          backgroundColor: "white",
                          padding: "6px 12px",
                          "&:hover": {
                            borderColor: "#fff",
                          },
                        },
                      }}
                    />
                  )}
                />
              </>
            )}
            <DialogActions
              sx={{
                padding: { xs: "1rem", md: "1.5rem" },
              }}
            >
              <Button
                onClick={onClose}
                variant="secondary"
                variantType="outlined"
                label="Cancel"
                style={{
                  marginRight: "10px",
                }}
              />
              <Button
                isLoading={isLoading}
                disabled={!(dirty && isValid)}
                type="submit"
                variant="secondary"
                variantType="contained"
                label="Send Request"
              />
            </DialogActions>
          </Form>
        )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default ManageDomainsModal;